import { useState, useCallback, lazy, Suspense } from 'react';
import Sidebar from '@/components/Sidebar';
import Header from '@/components/Header';
import { LayoutProvider } from '@/contexts/LayoutContext';
import OnboardingAssistant from '@/components/assistant/OnboardingAssistant';
import { ProgressBar } from '@/components/shell-ui/ProgressBar';
import { PageInfoBanner } from '@/components/shell-ui/PageInfoBanner';
import { MenuTour } from '@/components/shell-ui/MenuTour';
import { CommandPalette } from '@/components/shell-ui/CommandPalette';
import { useInstallNotifyBridge } from '@/stores/pushedNotifications';
import { useAuth } from '@/contexts/AuthContext';
import PlanThemeApplier from '@/components/PlanThemeApplier';

// Floating Brew ESG assistant (slide-in drawer), loaded from the brew_mfe remote.
// A failed remote load must never break the app, so fall back to a no-op.
const BrewWidget = lazy(() =>
  import('brew_mfe/BrewWidget').catch(() => ({ default: () => null })),
);

// Brew connector check disabled — brew's /api/admin/connectors endpoint was
// returning 404 + missing CORS headers, spamming the console on every page
// load. Widget falls back to "always on" (super-admin's per-org kill switch
// below still applies). Restore the fetch (see git history) once brew's
// endpoint is stable and returns Access-Control-Allow-Origin for esgcaffe.ai.
function useEsgConnectorEnabled(): boolean {
  return true;
}

interface AppLayoutProps {
  children: React.ReactNode;
}

/**
 * AppLayout - Shared layout wrapper for all authenticated pages
 *
 * This component keeps Sidebar and Header mounted across route changes,
 * preventing the blink/flash when navigating between pages.
 * Only the children (page content) changes on navigation.
 *
 * Uses LayoutProvider to signal to child components that layout is already provided,
 * preventing duplicate sidebar/header rendering in pages that still have old code.
 */
const AppLayout = ({ children }: AppLayoutProps) => {
  // Expose `window.esgcaffe.notify(...)` so any federated MFE can push a
  // notification into the shell bell without importing shell code.
  useInstallNotifyBridge();

  const brewEnabled = useEsgConnectorEnabled();
  // Super-admin per-org kill switch. Falls open: if the flag is missing
  // (older profile shapes, super-admin unreachable) Brew AI stays on.
  const { user } = useAuth();
  const brewAiAllowed = (user as { tenantSettings?: { disableBrewAi?: boolean } } | null)
    ?.tenantSettings?.disableBrewAi !== true;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('sidebar-collapsed', String(!prev));
      return !prev;
    });
  }, []);

  return (
    <LayoutProvider>
      <PlanThemeApplier />
      <div data-theme-part="page" className="h-screen overflow-hidden bg-slate-50 dark:bg-slate-950">
        {/* Top loading slider — shown while any react-query fetch/mutation
            or imperative fetch() call is in flight, across every MFE. */}
        <ProgressBar />

        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          forceRender
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
        />
        <Header
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          forceRender
          sidebarCollapsed={sidebarCollapsed}
        />

        {/* Main Content Area - only this part changes on navigation */}
        <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-[260px]'} pt-16 h-screen overflow-y-auto overflow-x-hidden transition-[margin] duration-300`}>
          {/* "About this page" banner — content sourced per-menu from
              apps/shell/src/config/menuInfo.ts. Renders nothing for menus
              without a registry entry. */}
          <PageInfoBanner />
          {children}
        </main>

        {/* First-visit onboarding tour, per top-level menu.
            Steps live in the same menuInfo registry as the banner. */}
        <MenuTour />

        {/* Ctrl/⌘+K command palette — indexes the current user's sidebar
            menus so search works from every module. MFEs with their own
            palette (PCF, compliance) opt out via a pathname check inside
            the palette component. */}
        <CommandPalette />

        {/* <OnboardingAssistant /> */}

        {/* Floating Brew ESG assistant — shown only when the ESG Caffe
            connector is enabled in the Brew engine AND the super-admin has
            not disabled Brew AI for this tenant. */}
        {brewEnabled && brewAiAllowed && (
          <Suspense fallback={null}>
            <BrewWidget />
          </Suspense>
        )}
      </div>
    </LayoutProvider>
  );
};

export default AppLayout;
